import { useEffect, useState } from "react";
import {
  getScreenPopups,
  createScreenPopup,
  updateScreenPopup,
  toggleScreenPopup,
  deleteScreenPopup
} from "../../api/client";
import AdminNav from "../../components/AdminNav";

const SCREENS = [
  { value: "all", label: "All User Screens" },
  { value: "dashboard", label: "User Dashboard" },
  { value: "place_order", label: "Place Order / Booking" },
  { value: "track", label: "Track Delivery" },
  { value: "wallet", label: "User Wallet" },
  { value: "subscription", label: "Subscription Plans" },
  { value: "b2b", label: "B2B / Corporate Portal" },
  { value: "profile", label: "User Profile" }
];

export default function AdminScreenPopups() {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    screen: "dashboard",
    title: "",
    message: "",
    imageUrl: "",
    badge: "Special Notice",
    actionButtonText: "",
    actionButtonLink: "",
    isActive: true,
    priority: 0,
    dismissible: true
  });

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await getScreenPopups();
      setPopups(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load pop-up cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({
      screen: "dashboard",
      title: "",
      message: "",
      imageUrl: "",
      badge: "Special Notice",
      actionButtonText: "",
      actionButtonLink: "",
      isActive: true,
      priority: 0,
      dismissible: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (p) => {
    setEditingId(p._id);
    setForm({
      screen: p.screen,
      title: p.title,
      message: p.message,
      imageUrl: p.imageUrl || "",
      badge: p.badge || "Notice",
      actionButtonText: p.actionButtonText || "",
      actionButtonLink: p.actionButtonLink || "",
      isActive: p.isActive,
      priority: p.priority || 0,
      dismissible: p.dismissible !== false
    });
    setShowModal(true);
  };

  const handleToggle = async (id) => {
    try {
      const { data } = await toggleScreenPopup(id);
      setPopups((prev) => prev.map((p) => (p._id === id ? data : p)));
      setSuccess("Status updated");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Could not toggle pop-up");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pop-up card?")) return;
    try {
      await deleteScreenPopup(id);
      setPopups((prev) => prev.filter((p) => p._id !== id));
      setSuccess("Pop-up card deleted");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Could not delete pop-up");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        const { data } = await updateScreenPopup(editingId, form);
        setPopups((prev) => prev.map((p) => (p._id === editingId ? data : p)));
        setSuccess("Pop-up card updated successfully");
      } else {
        const { data } = await createScreenPopup(form);
        setPopups((prev) => [data, ...prev]);
        setSuccess("Pop-up card created successfully");
      }
      setShowModal(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save pop-up card");
    }
  };

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h2 style={{ margin: 0 }}>Screen Pop-Up Cards</h2>
            <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", fontSize: "14px" }}>
              Configure announcements, seasonal bonuses, or alerts shown to users on specific screens. Toggle active on/off anytime.
            </p>
          </div>
          <button onClick={handleOpenCreate} style={{ padding: "10px 18px", fontWeight: 600 }}>
            + Create Pop-Up Card
          </button>
        </div>

        {error && <div className="error" style={{ marginBottom: "16px" }}>{error}</div>}
        {success && <div className="status-badge" style={{ marginBottom: "16px", display: "inline-block" }}>{success}</div>}

        {loading ? (
          <div className="empty-state"><p className="empty-state-text waiting-dots">Loading pop-up cards</p></div>
        ) : popups.length === 0 ? (
          <div className="empty-state" style={{ padding: "40px", textAlign: "center" }}>
            <span style={{ fontSize: "36px", display: "block", marginBottom: "12px" }}>📢</span>
            <p className="empty-state-text">No pop-up cards created yet.</p>
            <button onClick={handleOpenCreate} style={{ marginTop: "12px" }}>Create First Card</button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "16px" }}>
            {popups.map((p) => {
              const screenLabel = SCREENS.find((s) => s.value === p.screen)?.label || p.screen;
              return (
                <div
                  key={p._id}
                  style={{
                    background: "var(--bg-card, rgba(255, 255, 255, 0.04))",
                    border: p.isActive ? "1px solid rgba(22, 199, 132, 0.3)" : "1px solid var(--border)",
                    borderRadius: "12px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative"
                  }}
                >
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      <span className="status-badge info" style={{ fontSize: "11px" }}>{screenLabel}</span>
                      <button
                        onClick={() => handleToggle(p._id)}
                        style={{
                          background: p.isActive ? "rgba(22, 199, 132, 0.15)" : "rgba(239, 68, 68, 0.15)",
                          color: p.isActive ? "var(--success, #16c784)" : "var(--danger, #ef4444)",
                          border: `1px solid ${p.isActive ? "rgba(22, 199, 132, 0.3)" : "rgba(239, 68, 68, 0.3)"}`,
                          borderRadius: "20px",
                          padding: "4px 12px",
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer"
                        }}
                      >
                        {p.isActive ? "Active (ON)" : "Disabled (OFF)"}
                      </button>
                    </div>

                    <div style={{ textAlign: "center", padding: "8px 0" }}>
                      {p.badge && (
                        <div style={{ marginBottom: "6px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--warning, #f5a623)", textTransform: "uppercase", background: "rgba(245, 166, 35, 0.12)", padding: "3px 8px", borderRadius: "12px" }}>
                            {p.badge}
                          </span>
                        </div>
                      )}

                      <h4 style={{ margin: "6px 0", fontSize: "16px", color: "#fff" }}>{p.title}</h4>
                      <p style={{ margin: "0 auto 12px", maxWidth: "90%", fontSize: "13px", color: "var(--text-secondary)", lineHeight: 1.4, maxHeight: "60px", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.message}
                      </p>

                      {p.actionButtonText && (
                        <div style={{ fontSize: "12px", color: "var(--text-accent)", marginBottom: "8px" }}>
                          CTA Button: <strong style={{ background: "rgba(37, 99, 235, 0.15)", padding: "2px 6px", borderRadius: "4px" }}>{p.actionButtonText}</strong>
                          <span style={{ fontSize: "11px", color: "var(--text-muted)", marginLeft: "6px" }}>({p.actionButtonText.length}/25 chars)</span>
                          {p.actionButtonLink && <span> → {p.actionButtonLink}</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "8px", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                    <button
                      className="secondary"
                      onClick={() => handleOpenEdit(p)}
                      style={{ flex: 1, padding: "6px 12px", fontSize: "13px" }}
                    >
                      Edit
                    </button>
                    <button
                      className="danger"
                      onClick={() => handleDelete(p._id)}
                      style={{ flex: 1, padding: "6px 12px", fontSize: "13px" }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create / Edit Modal */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999,
              padding: "16px"
            }}
          >
            <div
              style={{
                background: "var(--bg-main, #141724)",
                border: "1px solid var(--border)",
                borderRadius: "14px",
                width: "100%",
                maxWidth: "520px",
                padding: "24px",
                maxHeight: "90vh",
                overflowY: "auto"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ margin: 0 }}>{editingId ? "Edit Pop-Up Card" : "New Pop-Up Card"}</h3>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: "18px", cursor: "pointer" }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-field" style={{ marginBottom: "14px" }}>
                  <label>Target Screen</label>
                  <select
                    value={form.screen}
                    onChange={(e) => setForm({ ...form, screen: e.target.value })}
                    style={{ width: "100%", padding: "10px", background: "var(--bg-card)", color: "#fff", border: "1px solid var(--border)", borderRadius: "8px" }}
                  >
                    {SCREENS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-field" style={{ marginBottom: "14px" }}>
                  <label>Badge Label (Optional)</label>
                  <input
                    type="text"
                    value={form.badge}
                    placeholder="e.g. Special Offer, Notice, Promo"
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  />
                </div>

                <div className="form-field" style={{ marginBottom: "14px" }}>
                  <label>Title *</label>
                  <input
                    type="text"
                    required
                    value={form.title}
                    placeholder="e.g. Welcome Bonus of ₦1,000 Active!"
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>

                <div className="form-field" style={{ marginBottom: "14px" }}>
                  <label>Message Content *</label>
                  <textarea
                    rows={4}
                    required
                    value={form.message}
                    placeholder="Detailed announcement or promo message shown inside the card..."
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    style={{ width: "100%", padding: "10px", background: "var(--bg-input)", color: "#fff", border: "1px solid var(--border)", borderRadius: "8px" }}
                  />
                </div>

                <div className="form-field" style={{ marginBottom: "14px" }}>
                  <label>Image URL (Optional)</label>
                  <input
                    type="url"
                    value={form.imageUrl}
                    placeholder="https://.../banner.png"
                    onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                  <div className="form-field">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <label style={{ margin: 0 }}>Action Button Text</label>
                      <span style={{ fontSize: "11px", color: (form.actionButtonText?.length || 0) >= 25 ? "var(--warning, #f5a623)" : "var(--text-muted)" }}>
                        {form.actionButtonText?.length || 0}/25 chars
                      </span>
                    </div>
                    <input
                      type="text"
                      maxLength={25}
                      value={form.actionButtonText}
                      placeholder="e.g. Claim Now, Upgrade"
                      onChange={(e) => setForm({ ...form, actionButtonText: e.target.value })}
                    />
                    <small style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "3px" }}>
                      Limit 25 characters to keep modal buttons centrally balanced.
                    </small>
                  </div>
                  <div className="form-field">
                    <label>Action Link</label>
                    <input
                      type="text"
                      value={form.actionButtonLink}
                      placeholder="e.g. /wallet or /subscription"
                      onChange={(e) => setForm({ ...form, actionButtonLink: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "20px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                    Active (ON)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="checkbox"
                      checked={form.dismissible}
                      onChange={(e) => setForm({ ...form, dismissible: e.target.checked })}
                    />
                    User Can Dismiss
                  </label>
                </div>

                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit">
                    {editingId ? "Save Changes" : "Create Card"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
